import {
  enumUnit,
  InventoryLevelSortOption,
  InventoryViewMode,
  LocationFilterType,
} from '@rematter/frontend-lib-api-client';

import { InventoryLevelRecord } from '../types/inventoryLevel';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const inventoryLevelsQueryAtom = createQueryAtomFactory(
  'inventory.inventoryLevels',
  (params: {
    itemIds?: string[];
    facilityIds?: string[];
    zoneIds?: string[];
    searchTerm?: string;
    viewMode?: InventoryViewMode;
    locationFilterType?: LocationFilterType;
    hideZeroQuantity?: boolean;
    limit?: number;
    offset?: number;
    sorting?: InventoryLevelSortOption[];
  }) => ({
    inventoryLevels: {
      __args: {
        filter: {
          itemIds: params.itemIds,
          facilityIds: params.facilityIds,
          zoneIds: params.zoneIds,
          searchTerm: params.searchTerm,
          viewMode: params.viewMode,
          locationFilterType: params.locationFilterType,
          hideZeroQuantity: params.hideZeroQuantity,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        itemId: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
          measurementUnit: true,
          itemGroup: { id: true, name: true, number: true },
        },
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        quantity: {
          value: true,
          unit: true,
        },
        currencyValues: {
          currencyCode: true,
          totalValue: true,
          weightedAverageCost: true,
        },
        ownerAccountId: true,
        ownerAccount: {
          id: true,
          name: true,
        },
        processorAccountId: true,
        processorAccount: {
          id: true,
          name: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      levels: InventoryLevelRecord[];
      totalCount: number;
    } => {
      if (!data || !data.inventoryLevels || !data.inventoryLevels.entries) {
        return {
          levels: [],
          totalCount: 0,
        };
      }

      const levels = data.inventoryLevels.entries.map((level) => ({
        id: [level.itemId, level.facilityId, level.ownerAccountId]
          .filter(Boolean)
          .join('-'),
        itemId: level.itemId,
        itemName: level.item?.name || '',
        itemSku: level.item?.skuNumber || '',
        itemMeasurementUnit: level.item?.measurementUnit || 'Pounds',
        itemGroupId: level.item?.itemGroup?.id || undefined,
        itemGroupName: level.item?.itemGroup?.name || undefined,
        itemGroupNumber: level.item?.itemGroup?.number || undefined,
        facilityId: level.facilityId || undefined,
        facilityName: level.facility?.facilityName || undefined,
        quantity: Number(level.quantity?.value) || 0,
        quantityUnit: level.quantity?.unit || enumUnit.Pounds,
        currencyValues: (level.currencyValues || []).map((cv) => ({
          currencyCode: cv.currencyCode,
          totalValue: cv.totalValue != null ? Number(cv.totalValue) : null,
          weightedAverageCost:
            cv.weightedAverageCost != null
              ? Number(cv.weightedAverageCost)
              : null,
        })),
        ownerAccountId: level.ownerAccountId || undefined,
        ownerAccountName: level.ownerAccount?.name || undefined,
        processorAccountId: level.processorAccountId || undefined,
        processorAccountName: level.processorAccount?.name || undefined,
      }));

      return {
        levels,
        totalCount: data.inventoryLevels.page?.total || 0,
      };
    },
  },
);
